/**
 * The TICKET block of the Context tab — task 5 (Tossker repaint), README §5
 * "Context pane": a featured card (3px `--brand-accent` TOP rule, never a
 * coloured left border — the left rule belongs to the rail/floor's
 * ownership cards, not this one), the "ALSO ON THIS TICKET · N" sibling list
 * with the live one tinted `--teal-50` and carrying its own mm:ss clock, the
 * close-semantics note, the held-sync chip, and "Sync now" / "Change" (or
 * the dashed no-ticket card's "Attach a ticket"). Every field comes from the
 * `ThreadDetail` the parent fetched from `ops.thread()` — nothing here is
 * invented, per resolution #1: a manually-linked ticket always shows the
 * honest "Linked manually · subject loads from Halo" line, never a guessed
 * subject.
 */
import type { ThreadDetail } from '@communicator/shared'
import { Button, Card, Icon } from '../ui'
import { activateOnKey, CHANNEL_ICON, CHANNEL_ICON_VAR, CHANNEL_LABEL, livenessLabel } from '../rail/format'
import { NO_TICKET_TEXT, pendingSyncLabel, pendingSyncVisible, TICKET_MANUAL_META } from './format'

export interface TicketSectionProps {
  detail: ThreadDetail
  haloUp: boolean
  onSelectSibling: (id: string) => void
  onForceSync: () => void
  onChangeTicket: () => void
  onAttachTicket: () => void
}

/**
 * A live sibling's own elapsed clock — README §5: "the live one tinted
 * `--teal-50' with its clock." `Sibling` (packages/shared/src/types.ts,
 * `ThreadDetail.siblings`) carries `waitS` but no dedicated formatter of its
 * own; duplicated in miniature here rather than imported from `../thread/
 * format.ts`'s `formatClock`, for the same pane-decoupling reason
 * `./format.ts`'s own `formatCallClock` gives (see that file's doc comment).
 */
function siblingClock(waitS: number): string {
  const s = Math.max(0, Math.floor(waitS))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export function TicketSection({
  detail,
  haloUp,
  onSelectSibling,
  onForceSync,
  onChangeTicket,
  onAttachTicket,
}: TicketSectionProps) {
  const hasTicket = detail.ticket !== null

  return (
    <div className="flex flex-col gap-[7px]">
      <div className="text-[10px] font-semibold tracking-[.08em] text-lo">TICKET</div>

      {hasTicket ? (
        <Card
          className="flex flex-col gap-1.5 border-t-[3px] p-[11px]"
          style={{ borderTopColor: 'var(--brand-accent)' }}
        >
          <div className="font-mono text-[14px] font-semibold text-hi" data-testid="ticket-title">
            {detail.ticket}
          </div>
          <div className="text-[11px] leading-[1.45] text-mid" data-testid="ticket-meta">
            {TICKET_MANUAL_META}
          </div>

          {detail.siblings.length > 0 ? (
            <div className="mt-0.5 flex flex-col gap-1.5" data-testid="sibling-list">
              <div className="text-[10px] font-semibold tracking-[.07em] text-lo">
                ALSO ON THIS TICKET · {detail.siblings.length}
              </div>
              {detail.siblings.map((sb) => {
                const isLive = sb.liveness === 'live'
                return (
                  <div
                    key={sb.id}
                    data-testid={`sibling-${sb.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectSibling(sb.id)}
                    onKeyDown={(e) => activateOnKey(e, () => onSelectSibling(sb.id))}
                    className={[
                      'flex cursor-pointer items-center gap-[9px] rounded-[9px] border border-subtle px-[9px] py-2',
                      // README §5: the live sibling is tinted `--teal-50`
                      // specifically — that's `--tint-primary` (Tossker Day),
                      // NOT `--brand-primary-soft` (`bg-cc-blue-pale`, teal-100
                      // — a different, darker step). See this task's report
                      // for the "map the literal to its real semantic slot"
                      // caution this repaint keeps tripping on.
                      //
                      // Fix round 1 (coordinator review): the non-live branch
                      // was `bg-elevated` — the raised-surface token, a
                      // visible grey box on Tossker Night. This row already
                      // has its own `border-subtle` for separation from the
                      // card; `bg-sunken` (inset, not raised) is the right
                      // neutral fill alongside it.
                      isLive ? 'bg-tint-primary' : 'bg-sunken',
                    ].join(' ')}
                  >
                    <div
                      className="flex h-5 w-5 flex-none items-center justify-center rounded-md"
                      style={{ color: isLive ? undefined : `var(${CHANNEL_ICON_VAR[sb.channel]})` }}
                    >
                      <Icon
                        name={CHANNEL_ICON[sb.channel]}
                        label={CHANNEL_LABEL[sb.channel]}
                        size={11}
                        className={isLive ? 'text-cc-blue-text' : ''}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[11px] font-semibold text-hi">{sb.who}</div>
                      <div className="mt-[1px] truncate text-[10px] text-lo">
                        {livenessLabel(sb.liveness)} · {sb.owner ?? 'unclaimed'}
                      </div>
                    </div>
                    {isLive && (
                      <span
                        data-testid={`sibling-clock-${sb.id}`}
                        className="flex-none font-mono text-[11px] text-cc-blue-text"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {siblingClock(sb.waitS)}
                      </span>
                    )}
                  </div>
                )
              })}
              <div className="text-[10px] leading-[1.45] text-lo" data-testid="close-semantics-note">
                Closing this thread leaves the others live. Closing {detail.ticket} in Halo closes all of them.
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-cc-blue-text" data-testid="no-siblings-note">
              This is the only live thread on the ticket.
            </div>
          )}

          {pendingSyncVisible(detail.heldWrites, haloUp, hasTicket) && (
            <div
              data-testid="pending-sync-chip"
              className="self-start rounded-[5px] bg-warn-subtle px-[7px] py-[3px] text-[10px] font-semibold tracking-[.05em] text-warn"
            >
              {pendingSyncLabel(detail.heldWrites)}
            </div>
          )}

          <div className="mt-0.5 flex flex-wrap gap-[7px]">
            <Button variant="secondary" data-testid="force-sync-btn" onClick={onForceSync}>
              <Icon name="refresh-cw" size={12} />
              Sync now
            </Button>
            <Button variant="secondary" data-testid="change-ticket-btn" onClick={onChangeTicket}>
              Change
            </Button>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-2 rounded-card border border-dashed border-default bg-sunken p-[11px]" data-testid="no-ticket-card">
          <div className="text-[11px] leading-[1.5] text-mid">{NO_TICKET_TEXT}</div>
          <Button variant="secondary" data-testid="attach-ticket-btn" onClick={onAttachTicket}>
            Attach a ticket
          </Button>
        </div>
      )}
    </div>
  )
}
